import React, { useState, useRef, useEffect } from 'react';
import './ProgressiveImage.css';

/**
 * ProgressiveImage component displays a placeholder while the real image loads.
 * @param {string} src - The real image source.
 * @param {string} alt - The alt text for the image.
 * @param {string} [placeholder] - Optional placeholder image source.
 * @param {string} [className] - CSS class for the image element.
 * @param {object} [style] - Inline styles for the image element.
 * @param {boolean} [showPlaceholder] - Whether to show placeholder image (default: true).
 * @param {object} [props] - Additional props for the <img> tag.
 */
const ProgressiveImage = ({ src, alt, placeholder = '', className = '', style = {}, showPlaceholder = true, ...props }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [imageStartedLoading, setImageStartedLoading] = useState(false);
  const imgRef = useRef(null);

  // Check if this is a logo image
  const isLogoImage = className.includes('Logo') || className.includes('logo');

  // Reset states when src changes
  useEffect(() => {
    setLoaded(false);
    setError(false);
    setImageStartedLoading(false);
  }, [src]);

  // Set loading state when image starts loading
  useEffect(() => {
    if (src && !loaded && !error) {
      setImageStartedLoading(true);
    }
  }, [src, loaded, error]);

  const handleImageLoad = () => {
    setLoaded(true);
    setError(false);
    setImageStartedLoading(false);
  };

  const handleImageError = () => {
    setError(true);
    setLoaded(false);
    setImageStartedLoading(false);
  };

  // For logo images, we want a completely clean loading experience
  if (isLogoImage) {
    return (
      <div className="progressive-image-container logo-container">
        {/* Loading indicator - only show when image is loading */}
        {imageStartedLoading && !loaded && !error && (
          <div className="progressive-image-loading logo-loading">
            <div className="progressive-image-spinner" />
          </div>
        )}

        {/* Main image - completely hidden until loaded */}
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={handleImageLoad}
          onError={handleImageError}
          className={`progressive-image-main logo-main ${className}`}
          style={{
            ...style,
            opacity: loaded ? 1 : 0,
            visibility: loaded ? 'visible' : 'hidden',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: loaded ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) scale(0)',
            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
          }}
          {...props}
        />

        {/* Error state */}
        {error && (
          <div className="progressive-image-error logo-error">
            <div className="progressive-image-error-icon">📷</div>
          </div>
        )}
      </div>
    );
  }

  // For non-logo images, use the standard progressive loading
  const shouldShowPlaceholder = showPlaceholder && placeholder && placeholder.trim() !== '';

  return (
    <div className="progressive-image-container">
      {/* Placeholder image - only for non-logo images */}
      {shouldShowPlaceholder && !loaded && !error && (
        <img
          src={placeholder}
          alt=""
          className={`progressive-image-placeholder ${className}`}
          style={{
            ...style,
            opacity: (loaded || error) ? 0 : 1,
          }}
        />
      )}
      
      {/* Loading indicator */}
      {imageStartedLoading && !loaded && !error && (
        <div 
          className="progressive-image-loading"
          style={{ opacity: loaded ? 0 : 1 }}
        >
          <div className="progressive-image-spinner" />
        </div>
      )}

      {/* Main image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
        className={`progressive-image-main ${className}`}
        style={{
          ...style,
          opacity: loaded ? 1 : 0,
          visibility: loaded ? 'visible' : 'hidden',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
        {...props}
      />

      {/* Error state */}
      {error && (
        <div className="progressive-image-error">
          <div className="progressive-image-error-icon">📷</div>
        </div>
      )}
    </div>
  );
};

export default ProgressiveImage;
